import type { FC } from 'react';
import { useState, useMemo } from 'react';
import {
  DndContext,
  useSensor,
  useSensors,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  type DragEndEvent,
  type DragStartEvent,
  rectIntersection,
} from '@dnd-kit/core';
import { MoreVertical, MoveRight, Clock } from 'lucide-react';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { DevelopmentStrategy } from '../../types';
import { Modal } from '../ui/Modal';
import { ActionCard } from './ActionCard';
import { DroppableColumn } from './DroppableColumn';
import { ActionPlanDetail } from './ActionPlanDetail';
import { DifficultyBadge, CategoryBadge, DueDateBadge } from '../ui/ActionItemBadges';
import { useAuthStore } from '../../store/authStore';

export type ActionStatus = 'todo' | 'inprogress' | 'done';

export interface ActionItem {
  id: string;
  title: string;
  description: string;
  status: ActionStatus;
  category?: string;
  difficulty?: string;
  timeframe?: string;
}

const parseTimeframe = (timeframe: string): Date | null => {
  if (!timeframe) return null;
  const now = new Date();
  const parts = timeframe.split(' ');
  const amount = parseInt(parts[0], 10);
  const unit = parts[1].toLowerCase();
  if (isNaN(amount)) return null;
  switch (unit) {
    case 'dia': case 'dias': now.setDate(now.getDate() + amount); break;
    case 'semana': case 'semanas': now.setDate(now.getDate() + amount * 7); break;
    case 'mês': case 'meses': now.setMonth(now.getMonth() + amount); break;
    default: return null;
  }
  return now;
};

interface ActionPlanSectionProps {
  strategies: DevelopmentStrategy[];
}

export const ActionPlanSection: FC<ActionPlanSectionProps> = ({ strategies }) => {
  const { user } = useAuthStore();
  
  // Verificar se o usuário é admin
  /*const isAdmin = user?.roles?.includes('admin') || 
                  user?.Roles?.includes('admin') || 
                  user?.customClaims?.admin === true;*/
  const isAdmin = true; // Forçando admin para testes

  // Se não for admin, mostrar mensagem "Em Breve"
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/10">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Plano de Ação Interativo</h2>
          <p className="text-slate-600 mb-6 leading-relaxed">
            Esta funcionalidade está sendo finalizada e estará disponível em breve. 
            O plano de ação interativo permitirá que você organize e acompanhe suas tarefas de desenvolvimento.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Em desenvolvimento</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const [itemsByStatus, setItemsByStatus] = useState(() => {
    const initialItems = (strategies || []).map((strategy): ActionItem => ({
      id: strategy.id,
      title: strategy.titulo,
      description: strategy.descricao,
      status: 'todo' as ActionStatus, // Type assertion explícita
      category: strategy.categoria,
      difficulty: strategy.complexidade || 'Médio',
      timeframe: strategy.prazo_implementacao,
    }));
    
    const result = { 
      todo: [] as ActionItem[], 
      inprogress: [] as ActionItem[], 
      done: [] as ActionItem[] 
    };
    
    initialItems.forEach(item => {
      result[item.status].push(item);
    });
    
    return result;
  });

  const [selectedAction, setSelectedAction] = useState<ActionItem | null>(null);
  const [activeItem, setActiveItem] = useState<ActionItem | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const allItems = useMemo(() => Object.values(itemsByStatus).flat(), [itemsByStatus]);

  const findContainer = (id: string): ActionStatus | undefined => {
    if (id in itemsByStatus) return id as ActionStatus;
    return allItems.find((item) => item.id === id)?.status;
  };

  const isColumnId = (id: string): id is ActionStatus => ['todo', 'inprogress', 'done'].includes(id);

  const sensors = useSensors(
    useSensor(PointerSensor, { 
      activationConstraint: { 
        distance: 8, // Reduzido de 3 para 8 pixels para evitar ativação acidental
        delay: 0,    // Removido delay
        tolerance: 0 // Removido tolerance
      } 
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const item = allItems.find(i => i.id === event.active.id);
    if (item) {
      setActiveItem(item);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveItem(null);
    
    if (!over) {
      return;
    }
    
    const activeId = String(active.id);
    const overId = String(over.id);
    
    if (activeId === overId) return;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId) || (isColumnId(overId) ? overId : undefined);
    
    if (!activeContainer || !overContainer) {
      return;
    }
    
    if (activeContainer === overContainer) {
      setItemsByStatus((prev) => {
        const activeItems = prev[activeContainer];
        if (!activeItems) return prev;
        const activeIndex = activeItems.findIndex((item: ActionItem) => item.id === activeId);
        const overIndex = activeItems.findIndex((item: ActionItem) => item.id === overId);
        if (activeIndex === -1 || overIndex === -1) return prev;
        return { ...prev, [activeContainer]: arrayMove(activeItems, activeIndex, overIndex) };
      });
    } else {
      setItemsByStatus((prev) => {
        const sourceItems = prev[activeContainer] ? [...prev[activeContainer]] : [];
        const destinationItems = prev[overContainer] ? [...prev[overContainer]] : [];
        const activeIndex = sourceItems.findIndex((item: ActionItem) => item.id === activeId);
        
        if (activeIndex === -1) {
          return prev;
        }
        
        const [movedItem] = sourceItems.splice(activeIndex, 1);
        movedItem.status = overContainer;
        
        // Se estamos dropando em uma coluna vazia, adicionar ao final
        let overIndex = destinationItems.findIndex((item: ActionItem) => item.id === overId);
        if (overIndex < 0) overIndex = destinationItems.length;
        
        destinationItems.splice(overIndex, 0, movedItem);
        
        const newState = { ...prev, [activeContainer]: sourceItems, [overContainer]: destinationItems };
        return newState;
      });
    }
  };

  const handleSelectAction = (actionId: string) => {
    const action = allItems.find(item => item.id === actionId);
    setSelectedAction(action || null);
    setIsDropdownOpen(false); // Fecha o dropdown ao abrir o modal
  };

  const handleCloseModal = () => {
    setSelectedAction(null);
    setIsDropdownOpen(false); // Garante que o dropdown seja fechado
  };

  const handleChangeActionStatus = (actionId: string, newStatus: ActionStatus) => {
    setItemsByStatus((prev) => {
      const sourceStatus = findContainer(actionId);
      if (!sourceStatus || sourceStatus === newStatus) return prev;
      const newItemsByStatus = JSON.parse(JSON.stringify(prev));
      const sourceItems = newItemsByStatus[sourceStatus];
      const itemIndex = sourceItems.findIndex((item: ActionItem) => item.id === actionId);
      if (itemIndex === -1) return prev;
      const [itemToMove] = sourceItems.splice(itemIndex, 1);
      itemToMove.status = newStatus;
      newItemsByStatus[newStatus].push(itemToMove);
      return newItemsByStatus;
    });
    setSelectedAction(null);
  };

  // Component para ações do modal
  const renderModalActions = (action: ActionItem) => {
    const statusOptions = [
      { value: 'todo' as ActionStatus, label: 'Para Fazer', disabled: action.status === 'todo' },
      { value: 'inprogress' as ActionStatus, label: 'Em Andamento', disabled: action.status === 'inprogress' },
      { value: 'done' as ActionStatus, label: 'Concluído', disabled: action.status === 'done' },
    ];

    return (
      <div className="relative md:hidden"> {/* Apenas no mobile */}
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Ações do card"
        >
          <MoreVertical size={20} />
        </button>
        
        {isDropdownOpen && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsDropdownOpen(false)}
            />
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
              <div className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-100">
                Mover card para:
              </div>
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    if (!option.disabled) {
                      handleChangeActionStatus(action.id, option.value);
                    }
                    setIsDropdownOpen(false);
                  }}
                  disabled={option.disabled}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors ${
                    option.disabled 
                      ? 'text-gray-400 cursor-not-allowed bg-gray-50' 
                      : 'text-gray-700 hover:bg-gray-100 cursor-pointer'
                  }`}
                >
                  <MoveRight size={16} className={option.disabled ? 'text-gray-300' : 'text-gray-500'} />
                  <span>{option.label}</span>
                  {option.disabled && <span className="ml-auto text-xs text-gray-400">(atual)</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderActionCard = (item: ActionItem, isOverlay = false) => (
    <ActionCard key={item.id} action={item} onSelect={() => handleSelectAction(item.id)} isOverlay={isOverlay}>
      <div className="flex flex-wrap items-center gap-2 pt-2">
        {item.difficulty && <DifficultyBadge difficulty={item.difficulty} />}
        {item.category && <CategoryBadge category={item.category} />}
        {item.timeframe && <DueDateBadge dueDate={parseTimeframe(item.timeframe)} />}
      </div>
    </ActionCard>
  );

  return (
    <div>
      <h2 className="text-3xl font-bold mt-6 mb-12 text-center">Quadro de Tarefas</h2>
      <DndContext 
        sensors={sensors}
        collisionDetection={rectIntersection} // Mudou de closestCenter para rectIntersection para melhor detecção de colunas
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveItem(null)}
      >
        {/* Container com scroll horizontal para responsivo */}
        <div className="w-full overflow-x-auto pb-4">
          <div className="flex md:grid md:grid-cols-3 gap-6 min-w-full md:min-w-0" style={{ minWidth: 'fit-content' }}>
            {(['todo', 'inprogress', 'done'] as ActionStatus[]).map((status) => {
              const items = itemsByStatus[status] || [];
              const titleMap = { todo: 'Para Fazer', inprogress: 'Em Andamento', done: 'Concluído' };
              return (
                <div key={status} className="flex-none w-80 md:w-auto"> {/* Largura fixa no mobile para scroll horizontal */}
                  <DroppableColumn key={status} id={status} title={titleMap[status]}>
                    <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                      {items.map((item) => renderActionCard(item))}
                    </SortableContext>
                  </DroppableColumn>
                </div>
              );
            })}
          </div>
        </div>
        <DragOverlay>
          {activeItem ? <div className="transform rotate-2 scale-105 shadow-2xl">{renderActionCard(activeItem, true)}</div> : null}
        </DragOverlay>
      </DndContext>

      {selectedAction && (
        <Modal 
          title="Detalhes da Ação" 
          isOpen={!!selectedAction} 
          onClose={handleCloseModal}
          headerActions={renderModalActions(selectedAction)}
        >
          <ActionPlanDetail 
            action={selectedAction} 
            onClose={handleCloseModal}
            onChangeStatus={handleChangeActionStatus}
          />
        </Modal>
      )}
    </div>
  );
}; 